import { AuthErrorCode } from '@/enums/AuthErrorCode';
import type { SupportedLocale } from '@/features/i18n/logic';

import { resolveAuthErrorCode } from './errorPresentation';

const ERROR_MESSAGES: Record<SupportedLocale, Record<AuthErrorCode, string>> = {
    ru: {
        [AuthErrorCode.AuthRejected]: 'Не удалось завершить вход. Попробуйте ещё раз.',
        [AuthErrorCode.EmailInvalid]: 'Укажите корректный email.',
        [AuthErrorCode.EmailOtpInvalid]: 'Неверный email-код.',
        [AuthErrorCode.OtpDeliveryFailed]: 'Не удалось отправить код. Попробуйте ещё раз.',
        [AuthErrorCode.OtpInvalid]: 'Неверный код. Проверьте цифры и попробуйте ещё раз.',
        [AuthErrorCode.OtpNotFound]: 'Код истёк или не найден. Запросите новый код.',
        [AuthErrorCode.PhoneInUse]: 'Этот номер уже используется.',
        [AuthErrorCode.RecoveryCodeInvalid]: 'Неверный код восстановления.',
        [AuthErrorCode.RecoveryInvalidOrExpired]: 'Восстановление устарело. Начните заново.',
        [AuthErrorCode.RegistrationInvalidOrExpired]: 'Регистрация устарела. Начните заново.',
        [AuthErrorCode.RegistrationUnavailable]: 'Не удалось продолжить регистрацию. Запросите код ещё раз.',
        [AuthErrorCode.RequestFailed]: 'Не удалось выполнить запрос. Попробуйте ещё раз.',
        [AuthErrorCode.UserNotFound]: 'Аккаунт с таким номером не найден.',
        [AuthErrorCode.UsernameInvalid]: 'Выберите другое имя пользователя.',
    },
    en: {
        [AuthErrorCode.AuthRejected]: 'We could not complete the sign-in. Please try again.',
        [AuthErrorCode.EmailInvalid]: 'Enter a valid email.',
        [AuthErrorCode.EmailOtpInvalid]: 'The email code is incorrect.',
        [AuthErrorCode.OtpDeliveryFailed]: 'We could not send the code. Please try again.',
        [AuthErrorCode.OtpInvalid]: 'The code is incorrect. Check it and try again.',
        [AuthErrorCode.OtpNotFound]: 'The code expired or was not found. Request a new one.',
        [AuthErrorCode.PhoneInUse]: 'This phone number is already in use.',
        [AuthErrorCode.RecoveryCodeInvalid]: 'The recovery code is incorrect.',
        [AuthErrorCode.RecoveryInvalidOrExpired]: 'This recovery request expired. Start again.',
        [AuthErrorCode.RegistrationInvalidOrExpired]: 'This registration expired. Start again.',
        [AuthErrorCode.RegistrationUnavailable]: 'We could not continue the registration. Request the code again.',
        [AuthErrorCode.RequestFailed]: 'The request could not be completed. Please try again.',
        [AuthErrorCode.UserNotFound]: 'No account was found for this phone number.',
        [AuthErrorCode.UsernameInvalid]: 'Choose a different username.',
    },
    es: {
        [AuthErrorCode.AuthRejected]: 'No se pudo completar el acceso. Inténtalo de nuevo.',
        [AuthErrorCode.EmailInvalid]: 'Introduce un email válido.',
        [AuthErrorCode.EmailOtpInvalid]: 'El código de email es incorrecto.',
        [AuthErrorCode.OtpDeliveryFailed]: 'No se pudo enviar el código. Inténtalo de nuevo.',
        [AuthErrorCode.OtpInvalid]: 'El código es incorrecto. Revísalo e inténtalo de nuevo.',
        [AuthErrorCode.OtpNotFound]: 'El código caducó o no se encontró. Solicita uno nuevo.',
        [AuthErrorCode.PhoneInUse]: 'Este número ya está en uso.',
        [AuthErrorCode.RecoveryCodeInvalid]: 'El código de recuperación es incorrecto.',
        [AuthErrorCode.RecoveryInvalidOrExpired]: 'La recuperación caducó. Empieza de nuevo.',
        [AuthErrorCode.RegistrationInvalidOrExpired]: 'El registro caducó. Empieza de nuevo.',
        [AuthErrorCode.RegistrationUnavailable]: 'No se pudo continuar el registro. Solicita el código de nuevo.',
        [AuthErrorCode.RequestFailed]: 'No se pudo completar la solicitud. Inténtalo de nuevo.',
        [AuthErrorCode.UserNotFound]: 'No se encontró una cuenta para este número.',
        [AuthErrorCode.UsernameInvalid]: 'Elige otro nombre de usuario.',
    },
    fr: {
        [AuthErrorCode.AuthRejected]: 'Impossible de finaliser la connexion. Réessayez.',
        [AuthErrorCode.EmailInvalid]: 'Saisissez un email valide.',
        [AuthErrorCode.EmailOtpInvalid]: 'Le code email est incorrect.',
        [AuthErrorCode.OtpDeliveryFailed]: "Impossible d'envoyer le code. Réessayez.",
        [AuthErrorCode.OtpInvalid]: 'Le code est incorrect. Vérifiez-le puis réessayez.',
        [AuthErrorCode.OtpNotFound]: 'Le code a expiré ou est introuvable. Demandez-en un nouveau.',
        [AuthErrorCode.PhoneInUse]: 'Ce numéro est déjà utilisé.',
        [AuthErrorCode.RecoveryCodeInvalid]: 'Le code de récupération est incorrect.',
        [AuthErrorCode.RecoveryInvalidOrExpired]: 'Cette récupération a expiré. Recommencez.',
        [AuthErrorCode.RegistrationInvalidOrExpired]: 'Cette inscription a expiré. Recommencez.',
        [AuthErrorCode.RegistrationUnavailable]: "Impossible de poursuivre l'inscription. Demandez un nouveau code.",
        [AuthErrorCode.RequestFailed]: "La requête n'a pas pu aboutir. Réessayez.",
        [AuthErrorCode.UserNotFound]: "Aucun compte n'a été trouvé pour ce numéro.",
        [AuthErrorCode.UsernameInvalid]: "Choisissez un autre nom d'utilisateur.",
    },
    ar: {
        [AuthErrorCode.AuthRejected]: 'تعذر إكمال تسجيل الدخول. حاول مرة أخرى.',
        [AuthErrorCode.EmailInvalid]: 'أدخل بريداً إلكترونياً صالحاً.',
        [AuthErrorCode.EmailOtpInvalid]: 'رمز البريد غير صحيح.',
        [AuthErrorCode.OtpDeliveryFailed]: 'تعذر إرسال الرمز. حاول مرة أخرى.',
        [AuthErrorCode.OtpInvalid]: 'الرمز غير صحيح. راجعه ثم حاول مرة أخرى.',
        [AuthErrorCode.OtpNotFound]: 'انتهت صلاحية الرمز أو لم يتم العثور عليه. اطلب رمزاً جديداً.',
        [AuthErrorCode.PhoneInUse]: 'رقم الهاتف هذا مستخدم بالفعل.',
        [AuthErrorCode.RecoveryCodeInvalid]: 'رمز الاستعادة غير صحيح.',
        [AuthErrorCode.RecoveryInvalidOrExpired]: 'انتهت صلاحية الاستعادة. ابدأ من جديد.',
        [AuthErrorCode.RegistrationInvalidOrExpired]: 'انتهت صلاحية التسجيل. ابدأ من جديد.',
        [AuthErrorCode.RegistrationUnavailable]: 'تعذر متابعة التسجيل. اطلب الرمز مرة أخرى.',
        [AuthErrorCode.RequestFailed]: 'تعذر إكمال الطلب. حاول مرة أخرى.',
        [AuthErrorCode.UserNotFound]: 'لم يتم العثور على حساب لهذا الرقم.',
        [AuthErrorCode.UsernameInvalid]: 'اختر اسم مستخدم مختلفاً.',
    },
    'zh-CN': {
        [AuthErrorCode.AuthRejected]: '无法完成登录，请重试。',
        [AuthErrorCode.EmailInvalid]: '请输入有效邮箱。',
        [AuthErrorCode.EmailOtpInvalid]: '邮箱验证码不正确。',
        [AuthErrorCode.OtpDeliveryFailed]: '验证码发送失败，请重试。',
        [AuthErrorCode.OtpInvalid]: '验证码不正确，请检查后重试。',
        [AuthErrorCode.OtpNotFound]: '验证码已过期或不存在，请重新获取。',
        [AuthErrorCode.PhoneInUse]: '该手机号已被使用。',
        [AuthErrorCode.RecoveryCodeInvalid]: '恢复码不正确。',
        [AuthErrorCode.RecoveryInvalidOrExpired]: '恢复请求已过期，请重新开始。',
        [AuthErrorCode.RegistrationInvalidOrExpired]: '注册已过期，请重新开始。',
        [AuthErrorCode.RegistrationUnavailable]: '无法继续注册，请重新获取验证码。',
        [AuthErrorCode.RequestFailed]: '请求未能完成，请重试。',
        [AuthErrorCode.UserNotFound]: '未找到该手机号对应的账号。',
        [AuthErrorCode.UsernameInvalid]: '请选择其他用户名。',
    },
    'zh-HK': {
        [AuthErrorCode.AuthRejected]: '未能完成登入，請再試一次。',
        [AuthErrorCode.EmailInvalid]: '請輸入有效電郵。',
        [AuthErrorCode.EmailOtpInvalid]: '電郵驗證碼不正確。',
        [AuthErrorCode.OtpDeliveryFailed]: '未能傳送驗證碼，請再試一次。',
        [AuthErrorCode.OtpInvalid]: '驗證碼不正確，請檢查後再試。',
        [AuthErrorCode.OtpNotFound]: '驗證碼已過期或不存在，請重新取得。',
        [AuthErrorCode.PhoneInUse]: '此電話號碼已被使用。',
        [AuthErrorCode.RecoveryCodeInvalid]: '恢復碼不正確。',
        [AuthErrorCode.RecoveryInvalidOrExpired]: '恢復請求已過期，請重新開始。',
        [AuthErrorCode.RegistrationInvalidOrExpired]: '註冊已過期，請重新開始。',
        [AuthErrorCode.RegistrationUnavailable]: '未能繼續註冊，請重新取得驗證碼。',
        [AuthErrorCode.RequestFailed]: '未能完成請求，請再試一次。',
        [AuthErrorCode.UserNotFound]: '找不到此電話號碼對應的帳戶。',
        [AuthErrorCode.UsernameInvalid]: '請選擇其他使用者名稱。',
    },
};

export const getLocalizedAuthErrorMessage = ({
    error,
    locale,
    fallback,
    isRegistrationFlow = false,
}: {
    error: unknown;
    locale: SupportedLocale;
    fallback: string;
    isRegistrationFlow?: boolean;
}) => {
    const resolvedCode = resolveAuthErrorCode({
        error,
        isRegistrationFlow,
    });

    return ERROR_MESSAGES[locale][resolvedCode] ?? fallback;
};
